import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiMapPin,
  FiHeart,
  FiUsers,
  FiAward,
  FiTruck,
  FiFeather,
  FiGlobe,
  FiCheck,
  FiChevronRight,
  FiCoffee,
  FiSun,
  FiDroplet,
  FiUser,
  FiPackage,
  FiInstagram,
  FiTwitter,
  FiLinkedin
} from 'react-icons/fi';
import { MdAgriculture, MdEco } from 'react-icons/md';
import {
  GiCoolSpices,
  GiHotSpices,
  GiChiliPepper,
  GiHerbsBundle,
  GiPlantSeed,
} from 'react-icons/gi';
import './AboutPage.css';

const AboutPage = () => {
  const navigate = useNavigate();

  // Scroll to hash anchor when navigating from footer links
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, []);

  const sectionRefs = {
    hero: useRef(null),
    story: useRef(null),
    journey: useRef(null),
    values: useRef(null),
    team: useRef(null),
    impact: useRef(null)
  };
  
  const [activeSection, setActiveSection] = useState('hero');
  const [isVisible, setIsVisible] = useState({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-100px 0px' }
    );

    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line no-unused-vars
  const timelineData = [
    {
      year: '2022',
      quarter: 'Q1',
      title: 'The Dream Takes Root',
      description: 'Shamsa Mahir opens the first Shammy Spices storefront in Dar es Salaam, Tanzania with just 12 signature blends.',
      icon: <FiMapPin />,
      image: '🌱',
      achievements: ['First 100 customers', '12 signature blends', 'Local recognition']
    },
    {
      year: '2022',
      quarter: 'Q4',
      title: 'Growing Partnerships',
      description: 'Established direct trade relationships with 20+ local farms across Tanzania\'s spice-growing regions.',
      icon: <FiUsers />,
      image: '🤝',
      achievements: ['20+ farm partners', '3 regions', 'Fair trade certified']
    },
    {
      year: '2023',
      quarter: 'Q2',
      title: 'Quality Recognition',
      description: 'Achieved organic certification and expanded to 50+ farm partners. Launched our first spice bundles.',
      icon: <FiFeather />,
      image: '🌟',
      achievements: ['Organic certified', '50+ farms', '15 new products']
    },
    {
      year: '2024',
      quarter: 'Q1',
      title: 'Global Reach',
      description: 'Expanded internationally, bringing Tanzanian flavors to home cooks in 15+ countries worldwide.',
      icon: <FiGlobe />,
      image: '🌍',
      achievements: ['15+ countries', '10,000+ customers', 'International shipping']
    },
    {
      year: '2025',
      quarter: 'Present',
      title: 'Today & Beyond',
      description: 'Serving 15,000+ home cooks with authentic Tanzanian spices while preserving traditional farming methods.',
      icon: <FiHeart />,
      image: '❤️',
      achievements: ['15k+ customers', '75+ farm partners', 'Zero-waste goal']
    }
  ];

  // Enhanced values with more depth
  const valuesData = [
    {
      icon: <FiFeather />,
      title: 'Sustainability First',
      text: 'Carbon-neutral shipping, regenerative farming practices, and zero-waste packaging. We\'re committed to protecting the land that nourishes us.',
      stats: ['100% recyclable', 'Carbon neutral', 'Zero waste by 2026']
    },
    {
      icon: <FiUsers />,
      title: 'Fair Trade Always',
      text: 'Every farmer earns 40% above commodity rates through direct relationships. We believe in prosperity for all.',
      stats: ['40% above market', 'Direct trade', 'Community investment']
    },
    {
      icon: <FiAward />,
      title: 'Quality Obsession',
      text: 'Triple-lab-tested for purity and potency. If it doesn\'t make us smile, it doesn\'t ship. Simple as that.',
      stats: ['Triple tested', '100% pure', 'No fillers']
    },
    {
      icon: <FiTruck />,
      title: 'Fast & Fresh',
      text: 'Harvested, roasted, ground, and at your door within 21 days. That\'s the freshness guarantee.',
      stats: ['21-day freshness', 'Small batches', 'Preservative-free']
    },
    {
      icon: <GiCoolSpices />,
      title: 'Heritage Preserved',
      text: 'Traditional methods passed down through generations. Every spice tells a story of Tanzanian culture.',
      stats: ['3rd generation', 'Traditional methods', 'Cultural preservation']
    },
    {
      icon: <MdEco />,
      title: 'Biodiversity Focus',
      text: 'Supporting diverse ecosystems through regenerative agriculture and heirloom variety preservation.',
      stats: ['Heirloom varieties', 'Ecosystem support', 'Seed saving']
    }
  ];

  // eslint-disable-next-line no-unused-vars
  const teamData = [
    {
      name: 'Shamsa Mahir',
      role: 'Founder & Chief Curator',
      bio: 'Third-generation spice trader with a passion for preserving Tanzanian culinary heritage. Shamsa left a career in finance to bring her family\'s spice knowledge to the world.',
      initials: 'SM',
      color: '#1a4f0c',
      expertise: ['Spice blending', 'Farm partnerships', 'Cultural preservation'],
    },
    {
      name: 'James Omondi',
      role: 'Master Blender',
      bio: 'With 20+ years of experience in traditional spice preparation, James ensures every blend meets exacting standards passed down through generations.',
      initials: 'JO',
      color: '#2d7a1c',
      expertise: ['Traditional methods', 'Quality control', 'Recipe development'],
    },
    {
      name: 'Maria Kimaro',
      role: 'Sustainability Director',
      bio: 'Agricultural scientist leading our regenerative farming initiatives and farm partnership programs across Tanzania.',
      initials: 'MK',
      color: '#fabc2a',
      expertise: ['Regenerative ag', 'Farm education', 'Research'],
    },
  ];

  // Impact statistics
  const impactData = [
    {
      metric: 'Farm Partners',
      value: '75+',
      description: 'Direct relationships with smallholder farms',
      icon: <FiUsers />
    },
    {
      metric: 'Farmers Supported',
      value: '450+',
      description: 'Including family members and communities',
      icon: <FiHeart />
    },
    {
      metric: 'Acres Preserved',
      value: '1,200+',
      description: 'Under regenerative management',
      icon: <FiFeather />
    },
  ];

  // Process steps
  const processSteps = [
    {
      step: '01',
      title: 'Farm Partnership',
      description: 'We work directly with farmers who use traditional, sustainable methods passed down through generations.',
      icon: <MdAgriculture />
    },
    {
      step: '02',
      title: 'Hand Harvesting',
      description: 'Each spice is carefully hand-harvested at peak ripeness to capture maximum flavor and aroma.',
      icon: <FiSun />
    },
    {
      step: '03',
      title: 'Traditional Drying',
      description: 'Sun-dried using methods that preserve essential oils and natural compounds.',
      icon: <FiDroplet />
    },
    {
      step: '04',
      title: 'Small-Batch Roasting',
      description: 'Roasted in small batches to develop complex flavors while maintaining quality.',
      icon: <FiCoffee />
    },
    {
      step: '05',
      title: 'Stone Grinding',
      description: 'Traditional stone grinding preserves the spices\' integrity and releases their full aroma.',
      icon: <GiCoolSpices />
    },
    {
      step: '06',
      title: 'Quality Testing',
      description: 'Triple-lab-tested for purity, potency, and freshness before reaching your kitchen.',
      icon: <FiCheck />
    }
  ];

  return (
    <div className="about-page">
        {/* Progress bar */}
        <div className="reading-progress">
          <div 
            className="progress-bar" 
            style={{ width: `${(Object.values(isVisible).filter(Boolean).length / 6) * 100}%` }}
          />
        </div>

        {/* Section navigation */}
        <nav className="section-nav">
          {['hero', 'story', 'journey', 'values', 'team', 'impact'].map(section => (
            <button
              key={section}
              className={`nav-dot ${activeSection === section ? 'active' : ''}`}
              onClick={() => sectionRefs[section].current?.scrollIntoView({ behavior: 'smooth' })}
              aria-label={`Scroll to ${section}`}
            />
          ))}
        </nav>

        {/* HERO SECTION - Enhanced */}
        <section 
          id="hero"
          ref={sectionRefs.hero}
          className={`about-hero-section ${isVisible.hero ? 'visible' : ''}`}
        >
          <div className="hero-bg-pattern" />
          <div className="hero-bg-overlay" />
          
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-line" />
              <span className="badge-text">Since 2022</span>
              <span className="badge-line" />
            </div>

            <h1 className="hero-title">
              <span className="title-line">Bringing the</span>
              <span className="title-line accent">Heart of Tanzania</span>
              <span className="title-line">to Your Kitchen</span>
            </h1>

            <p className="hero-description">
              From the sun-drenched spice gardens of Zanzibar to the misty highlands of Mbeya, 
              we've spent three generations learning the art of spices. Now, we're sharing 
              that knowledge with you.
            </p>

            <div className="hero-stats-grid">
              <div className="hero-stat">
                <span className="stat-number">75+</span>
                <span className="stat-label">Farm Partners</span>
              </div>
              <div className="hero-stat">
                <span className="stat-number">50+</span>
                <span className="stat-label">Spice Varieties</span>
              </div>
              <div className="hero-stat">
                <span className="stat-number">15k+</span>
                <span className="stat-label">Happy Cooks</span>
              </div>
              <div className="hero-stat">
                <span className="stat-number">21</span>
                <span className="stat-label">Day Freshness</span>
              </div>
            </div>

            <div className="hero-cta">
              <button className="primary-cta" onClick={() => sectionRefs.story.current?.scrollIntoView({ behavior: 'smooth' })}>
                Discover Our Story
                <FiChevronRight />
              </button>
              <button className="secondary-cta" onClick={() => sectionRefs.values.current?.scrollIntoView({ behavior: 'smooth' })}>
                Our Values
              </button>
            </div>
          </div>

          <div className="hero-visual">
            <div className="spice-carousel">
              <div className="spice-item">
                <GiHerbsBundle />
                <span>Cinnamon</span>
              </div>
              <div className="spice-item">
                <GiPlantSeed />
                <span>Cardamom</span>
              </div>
              <div className="spice-item">
                <GiChiliPepper />
                <span>Chili</span>
              </div>
              <div className="spice-item">
                <GiHotSpices />
                <span>Spices</span>
              </div>
            </div>
          </div>
        </section>

        {/* STORY SECTION - Enhanced */}
        <section 
          id="story"
          ref={sectionRefs.story}
          className={`story-section ${isVisible.story ? 'visible' : ''}`}
        >
          <div className="story-container">
            <div className="story-media">
              <div className="media-grid">
                <div className="media-item main">
                  <div className="media-content">
                    <GiChiliPepper className="media-icon" />
                  </div>
                  <div className="media-caption">Dar es Salaam Spice Market, 2022</div>
                </div>
                <div className="media-item secondary">
                  <div className="media-content">
                    <GiHerbsBundle className="media-icon" />
                  </div>
                </div>
                <div className="media-item tertiary">
                  <div className="media-content">
                    <FiPackage className="media-icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="story-content">
              <div className="section-tag">
                <span className="tag-line" />
                <span className="tag-text">Our Origin</span>
              </div>

              <h2 className="story-headline">
                A Legacy of Flavor,
                <br />
                <span className="accent">A Future of Purpose</span>
              </h2>

              <div className="story-narrative">
                <p className="story-paragraph lead">
                  In 2022, I started Shammy Spices with a simple vision: to bring the authentic 
                  flavours of Tanzania to kitchens around the world. But the story begins long 
                  before that — in the bustling spice markets of Dar es Salaam, where I learned 
                  to distinguish cardamom by its scent and tell the age of cinnamon by its curl.
                </p>

                <p className="story-paragraph">
                  My grandmother was a spice trader in the 1960s, one of the few women in a 
                  male-dominated industry. She taught me that spices aren't just ingredients — 
                  they're the soul of our cuisine, carriers of culture, and keepers of memory. 
                  Every jar we create carries forward her legacy.
                </p>

                <div className="story-quote">
                  <FiHeart className="quote-icon" />
                  <blockquote>
                    "Spices are how we remember where we come from, and how we share it with others."
                  </blockquote>
                  <cite>— Shamsa Mahir</cite>
                </div>

                <p className="story-paragraph">
                  Today, we work directly with over 75 farmers across Tanzania, from the clove 
                  plantations of Zanzibar to the pepper vines of the Usambara Mountains. We pay 
                  fair prices, share knowledge, and work together to preserve traditional farming 
                  methods that have sustained communities for generations.
                </p>
              </div>

              <div className="story-signature-block">
                <div className="signature-image">
                  <FiUser className="signature-icon" />
                </div>
                <div className="signature-details">
                  <div className="signature-name">Shamsa Mohammed</div>
                  <div className="signature-title">Founder & Chief Curator</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS SECTION - New */}
        <section id="process" className="process-section">
          <div className="process-header">
            <div className="section-tag centered">
              <span className="tag-line" />
              <span className="tag-text">Our Craft</span>
              <span className="tag-line" />
            </div>
            <h2 className="process-title">From Farm to Jar</h2>
            <p className="process-subtitle">
              Every step is guided by tradition and refined by purpose.
            </p>
          </div>

          <div className="process-grid">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-icon">{step.icon}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* VALUES SECTION - Enhanced */}
        <section 
          id="values"
          ref={sectionRefs.values}
          className={`values-section ${isVisible.values ? 'visible' : ''}`}
        >
          <div className="values-bg" />
          
          <div className="values-header">
            <div className="section-tag centered light">
              <span className="tag-line" />
              <span className="tag-text">Our Principles</span>
              <span className="tag-line" />
            </div>
            <h2 className="values-title">What We Stand For</h2>
            <p className="values-subtitle">
              These aren't just words on a website — they're promises we keep every day.
            </p>
          </div>

          <div className="values-grid-enhanced">
            {valuesData.map((value, index) => (
              <div key={index} className="value-card-enhanced">
                <div className="value-icon-enhanced">{value.icon}</div>
                <h3 className="value-title-enhanced">{value.title}</h3>
                <p className="value-description">{value.text}</p>
                <div className="value-stats">
                  {value.stats.map((stat, i) => (
                    <div key={i} className="value-stat-item">
                      <FiCheck />
                      <span>{stat}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* IMPACT SECTION - New */}
        <section 
          id="impact"
          ref={sectionRefs.impact}
          className={`impact-section ${isVisible.impact ? 'visible' : ''}`}
        >
          <div className="impact-container">
            <div className="impact-header">
              <div className="section-tag centered">
                <span className="tag-line" />
                <span className="tag-text">Our Impact</span>
                <span className="tag-line" />
              </div>
              <h2 className="impact-title">Beyond Business</h2>
              <p className="impact-subtitle">
                Every purchase supports farmers, communities, and the environment.
              </p>
            </div>

            <div className="impact-grid">
              {impactData.map((item, index) => (
                <div key={index} className="impact-card">
                  <div className="impact-icon">{item.icon}</div>
                  <div className="impact-metric">{item.value}</div>
                  <h3 className="impact-card-title">{item.metric}</h3>
                  <p className="impact-description">{item.description}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* CTA SECTION - Enhanced */}
        <section className="cta-section">
          <div className="cta-container">
            <h2 className="cta-title">Ready to Bring Tanzania Home?</h2>
            <p className="cta-description">
              Explore our collection of authentic spices, curated for home cooks who care 
              about flavor, quality, and the stories behind their food.
            </p>
            <div className="cta-buttons">
              <button className="cta-primary" onClick={() => navigate('/shop')}>
                Shop Our Spices
                <FiChevronRight />
              </button>
              <button className="cta-secondary" onClick={() => navigate('/contact')}>
                Contact Us
              </button>
            </div>
            <div className="cta-social">
              <span>Follow our journey:</span>
              <div className="social-links">
                <button aria-label="Instagram" onClick={() => {}}><FiInstagram /></button>
                <button aria-label="Twitter" onClick={() => {}}><FiTwitter /></button>
                <button aria-label="LinkedIn" onClick={() => {}}><FiLinkedin /></button>
              </div>
            </div>
          </div>
        </section>
    </div>
  );
};

export default AboutPage;